import { Component } from "@/types";
import type { BaseChartProperties } from "@/types/chart-properties";

/**
 * Base interface for chart command mixins
 */
export interface ChartMixin {
  parseCommand(command: string, component: Component): Partial<BaseChartProperties> | null;
  getSuggestions?(component: Component): string[];
}

/**
 * Result type for mixin commands
 */
export type MixinResult = Partial<BaseChartProperties> | null;

/**
 * Utility to merge multiple mixin results
 */
export function mergeMixinResults(...results: MixinResult[]): MixinResult {
  const merged: Partial<BaseChartProperties> = {};

  for (const result of results) {
    if (result) {
      Object.assign(merged, result);
    }
  }

  return Object.keys(merged).length > 0 ? merged : null;
}

/**
 * Base class for chart mixins with common utilities
 */
export abstract class ChartMixinBase implements ChartMixin {
  abstract parseCommand(command: string, component: Component): MixinResult;

  /**
   * Extract numeric value from command
   */
  protected extractNumber(command: string, keywords: string[]): number | null {
    const pattern = new RegExp(
      `(?:${keywords.join('|')})\\s*(?:to|:|=)?\\s*(\\d+(?:\\.\\d+)?)`,
      'i'
    );
    const match = command.match(pattern);
    return match ? parseFloat(match[1]) : null;
  }

  /**
   * Extract percentage value from command
   */
  protected extractPercentage(command: string): number | null {
    const match = command.match(/(\d+(?:\.\d+)?)\s*%/);
    if (match) {
      return parseFloat(match[1]) / 100;
    }
    return null;
  }

  /**
   * Extract quoted string from command
   */
  protected extractQuotedString(command: string, keywords: string[]): string | null {
    const pattern = new RegExp(
      `(?:${keywords.join('|')})\\s*(?:to|:|=)?\\s*["']([^"']+)["']`,
      'i'
    );
    const match = command.match(pattern);
    return match ? match[1] : null;
  }

  /**
   * Check if command contains any of the keywords
   */
  protected hasKeywords(command: string, keywords: string[]): boolean {
    const lowerCommand = command.toLowerCase();
    return keywords.some(keyword => lowerCommand.includes(keyword.toLowerCase()));
  }
}
